import { useState, useEffect, type ReactNode } from 'react';
import Reveal from '@/components/base/Reveal';
import SmartLink from '@/components/base/SmartLink';
import Logo from '@/components/base/Logo';
import Footer from '@/pages/home/components/Footer';

function ScrollNavbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Our Mission', href: '/mission' },
    { label: 'What We Do', href: '/projects' },
    { label: 'About Us', href: '/about' },
  ];

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/90 backdrop-blur-md border-b border-white/5' : ''
      }`}
    >
      <div className="px-6 md:px-12 lg:px-16 pt-6 pb-4">
        <nav
          className={`rounded-xl px-4 py-2 flex items-center justify-between transition-all duration-300 ${
            scrolled ? 'bg-transparent' : 'liquid-glass'
          }`}
        >
          <SmartLink href="/">
            <Logo className="h-5 sm:h-6 md:h-7 w-auto text-white shrink-0" />
          </SmartLink>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <SmartLink
                key={link.label}
                href={link.href}
                className="text-sm text-white/80 hover:text-gray-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </SmartLink>
            ))}
          </div>
          <a href="mailto:hello@sapphiremedia.group">
            <button className="bg-white text-black px-3 py-1.5 sm:px-5 sm:py-2 md:px-6 rounded-lg text-xs sm:text-sm font-medium hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-1 sm:gap-1.5">
              Reach out
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" className="size-3.5 sm:size-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </button>
          </a>
        </nav>
      </div>
    </div>
  );
}

const siblings = [
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Terms of Service', href: '/terms' },
  { label: 'Code of Conduct', href: '/code-of-conduct' },
];

type LegalLayoutProps = {
  title: string;
  lastUpdated: string;
  current: '/privacy' | '/terms' | '/code-of-conduct';
  children: ReactNode;
};

export default function LegalLayout({ title, lastUpdated, current, children }: LegalLayoutProps) {
  return (
    <div className="bg-black min-h-screen">
      <ScrollNavbar />

      {/* Hero */}
      <section className="pt-44 pb-12 px-6 md:px-12 lg:px-16">
        <div className="max-w-3xl mx-auto">
          <Reveal variant="fadeUp" duration={800}>
            <p className="text-xs tracking-[0.3em] text-white/60 uppercase mb-6">Legal</p>
          </Reveal>
          <Reveal variant="fadeUp" delay={80} duration={900}>
            <h1
              className="text-5xl md:text-6xl font-normal text-white font-serif"
              style={{ letterSpacing: '-0.02em' }}
            >
              {title}
            </h1>
          </Reveal>
          <Reveal variant="fadeUp" delay={180} duration={800}>
            <p className="text-xs text-white/40 mt-6">Last updated · {lastUpdated}</p>
          </Reveal>
        </div>
      </section>

      {/* Sibling tabs */}
      <section className="px-6 md:px-12 lg:px-16">
        <div className="max-w-3xl mx-auto border-b border-white/5">
          <div className="flex gap-6 overflow-x-auto -mb-px">
            {siblings.map(s => {
              const active = s.href === current;
              return (
                <SmartLink
                  key={s.href}
                  href={s.href}
                  className={`text-xs whitespace-nowrap py-3 border-b transition-colors duration-200 cursor-pointer ${
                    active
                      ? 'text-white border-white/60'
                      : 'text-white/45 hover:text-white/75 border-transparent'
                  }`}
                >
                  {s.label}
                </SmartLink>
              );
            })}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="px-6 md:px-12 lg:px-16 pt-12 pb-28">
        <div className="max-w-3xl mx-auto">
          <Reveal variant="fadeUp" delay={120} duration={800}>
            <article className="legal-prose">{children}</article>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
